import React, { useState, useRef, useEffect, useMemo } from 'react'

export interface SearchableSelectOption {
  id: string
  label: string
  subLabel?: string
  badge?: string
}

interface Props {
  id?: string
  options: SearchableSelectOption[]
  value: string
  onChange: (id: string) => void
  placeholder?: string
  disabled?: boolean
  required?: boolean
  autoFocus?: boolean
  emptyMessage?: string
}

function normalizeSearchText(text: string): string {
  return text
    .toLocaleLowerCase('tr-TR')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ı/g, 'i')
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .trim()
}

export function SearchableSelect({
  id,
  options,
  value,
  onChange,
  placeholder = 'Aramak için yazın…',
  disabled = false,
  required = false,
  autoFocus = false,
  emptyMessage = 'Eşleşen sonuç bulunamadı.',
}: Props) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(0)

  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)

  const selectedOption = useMemo(
    () => options.find((opt) => opt.id === value),
    [options, value]
  )

  useEffect(() => {
    if (selectedOption) {
      setSearchTerm(selectedOption.badge ? `${selectedOption.badge} — ${selectedOption.label}` : selectedOption.label)
    } else {
      setSearchTerm('')
    }
  }, [selectedOption, value])

  const isSearching = useMemo(() => {
    if (!isOpen) return false
    if (!selectedOption) return searchTerm.trim().length > 0
    const selectedText = selectedOption.badge ? `${selectedOption.badge} — ${selectedOption.label}` : selectedOption.label
    return searchTerm.trim() !== selectedText.trim() && searchTerm.trim().length > 0
  }, [isOpen, selectedOption, searchTerm])

  const filteredOptions = useMemo(() => {
    if (!isSearching || !searchTerm.trim()) return options
    const term = searchTerm.trim()
    const normTerm = normalizeSearchText(term)
    const lowerTerm = term.toLocaleLowerCase('tr-TR')

    return options.filter((opt) => {
      const labelLower = opt.label.toLocaleLowerCase('tr-TR')
      const badgeLower = (opt.badge || '').toLocaleLowerCase('tr-TR')
      const subLower = (opt.subLabel || '').toLocaleLowerCase('tr-TR')
      if (labelLower.includes(lowerTerm) || badgeLower.includes(lowerTerm) || subLower.includes(lowerTerm)) {
        return true
      }
      const labelNorm = normalizeSearchText(opt.label)
      const badgeNorm = normalizeSearchText(opt.badge || '')
      return labelNorm.includes(normTerm) || badgeNorm.includes(normTerm)
    })
  }, [options, isSearching, searchTerm])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
        if (selectedOption) {
          setSearchTerm(selectedOption.badge ? `${selectedOption.badge} — ${selectedOption.label}` : selectedOption.label)
        } else {
          setSearchTerm('')
        }
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [selectedOption])

  useEffect(() => {
    if (highlightedIndex >= filteredOptions.length) {
      setHighlightedIndex(Math.max(0, filteredOptions.length - 1))
    }
  }, [filteredOptions.length, highlightedIndex])

  useEffect(() => {
    if (isOpen && highlightedIndex >= 0 && listRef.current) {
      const el = listRef.current.children[highlightedIndex] as HTMLElement
      if (el) {
        el.scrollIntoView({ block: 'nearest' })
      }
    }
  }, [isOpen, highlightedIndex])

  const handleSelect = (option: SearchableSelectOption) => {
    onChange(option.id)
    setSearchTerm(option.badge ? `${option.badge} — ${option.label}` : option.label)
    setIsOpen(false)
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange('')
    setSearchTerm('')
    setIsOpen(true)
    inputRef.current?.focus()
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setSearchTerm(val)
    setHighlightedIndex(0)
    if (!isOpen) setIsOpen(true)
    if (!val.trim()) {
      onChange('')
    }
  }

  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsOpen(true)
    if (selectedOption) {
      e.target.select()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        setIsOpen(true)
      }
      return
    }

    if (e.key === 'Escape') {
      e.preventDefault()
      setIsOpen(false)
      if (selectedOption) {
        setSearchTerm(selectedOption.badge ? `${selectedOption.badge} — ${selectedOption.label}` : selectedOption.label)
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightedIndex((prev) =>
        prev < filteredOptions.length - 1 ? prev + 1 : prev
      )
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0))
    } else if (e.key === 'Enter') {
      if (filteredOptions[highlightedIndex]) {
        e.preventDefault()
        handleSelect(filteredOptions[highlightedIndex])
      }
    }
  }

  return (
    <div
      ref={containerRef}
      className={`searchable-select-container ${isOpen ? 'is-open' : ''} ${disabled ? 'is-disabled' : ''}`}
      style={{ position: 'relative', width: '100%' }}
    >
      <input
        type="text"
        id={id}
        name={id}
        tabIndex={-1}
        required={required}
        value={value || ''}
        onChange={() => {}}
        style={{
          opacity: 0,
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: -1,
        }}
      />

      <div
        className="searchable-select-input-wrapper"
        onClick={() => {
          if (!disabled) {
            setIsOpen(true)
            inputRef.current?.focus()
          }
        }}
      >
        <span className="searchable-select-search-icon" aria-hidden="true">
          🔍
        </span>

        <input
          ref={inputRef}
          type="text"
          className="searchable-select-direct-input"
          placeholder={placeholder}
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          autoFocus={autoFocus}
          autoComplete="off"
          spellCheck={false}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        />

        <div className="searchable-select-actions">
          {value && !disabled && (
            <button
              type="button"
              className="searchable-select-clear-btn"
              onClick={handleClear}
              title="Seçimi temizle"
              aria-label="Seçimi temizle"
            >
              ✕
            </button>
          )}
          <span
            className={`searchable-select-caret ${isOpen ? 'open' : ''}`}
            onClick={(e) => {
              e.stopPropagation()
              if (!disabled) {
                setIsOpen((prev) => !prev)
                inputRef.current?.focus()
              }
            }}
          >
            ▼
          </span>
        </div>
      </div>

      {isOpen && (
        <div className="searchable-select-dropdown" role="listbox">
          <ul ref={listRef} className="searchable-select-list">
            {filteredOptions.length === 0 ? (
              <li className="searchable-select-empty">
                <span>{emptyMessage}</span>
              </li>
            ) : (
              filteredOptions.map((opt, index) => {
                const isSelected = opt.id === value
                const isHighlighted = index === highlightedIndex

                return (
                  <li
                    key={opt.id}
                    role="option"
                    aria-selected={isSelected}
                    className={`searchable-select-option ${isSelected ? 'is-selected' : ''} ${isHighlighted ? 'is-highlighted' : ''}`}
                    onClick={() => handleSelect(opt)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                  >
                    <div className="searchable-select-opt-left">
                      {opt.badge && <span className="searchable-select-code-badge">{opt.badge}</span>}
                      <span className="searchable-select-opt-name">{opt.label}</span>
                    </div>

                    <div className="searchable-select-opt-right">
                      {opt.subLabel && <span className="text-muted" style={{ fontSize: '0.8rem' }}>{opt.subLabel}</span>}
                      {isSelected && <span className="searchable-select-check">✓</span>}
                    </div>
                  </li>
                )
              })
            )}
          </ul>
        </div>
      )}
    </div>
  )
}
